import { formatMoney } from "theme";

const MAX_TIMEOUT = 2_147_483_647;
const promotionalPrices = new Set();
let boundaryTimer;

function schedulePromotionBoundary() {
  window.clearTimeout(boundaryTimer);

  const now = Date.now();
  let nextBoundary;

  for (const promotionalPrice of promotionalPrices) {
    const elementBoundary = promotionalPrice.getNextPromotionBoundary(now);

    if (elementBoundary && (!nextBoundary || elementBoundary < nextBoundary)) {
      nextBoundary = elementBoundary;
    }
  }

  if (!nextBoundary) {
    boundaryTimer = undefined;
    return;
  }

  const delay = Math.min(nextBoundary - now + 100, MAX_TIMEOUT);

  boundaryTimer = window.setTimeout(() => {
    for (const promotionalPrice of promotionalPrices) {
      promotionalPrice.refreshVisibility();
    }

    schedulePromotionBoundary();
  }, delay);
}

function registerPromotionalPrice(promotionalPrice) {
  promotionalPrices.add(promotionalPrice);
  schedulePromotionBoundary();
}

function unregisterPromotionalPrice(promotionalPrice) {
  promotionalPrices.delete(promotionalPrice);
  schedulePromotionBoundary();
}

class PromotionalPrice extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const formId = this.getAttribute("form");

    this.productForm = formId ? document.forms[formId] : null;
    this.priceElement = this.querySelector("[data-promotional-price]");
    this.discountPercentage = Number(this.getAttribute("discount-percentage"));
    this.activeFrom = Number(this.getAttribute("active-from")) || 0;
    this.activeUntil = Number(this.getAttribute("active-until")) || 0;
    this.hasSelectedVariant = !this.hasAttribute("start-unselected");

    if (!this.priceElement || !Number.isFinite(this.discountPercentage)) {
      return;
    }

    this.refreshVisibility();
    registerPromotionalPrice(this);

    this.productForm?.addEventListener("variant:change", (event) => this.updatePrice(event.detail.variant), {
      signal: this.abortController.signal,
    });
    this.productForm?.addEventListener("variant:selection-cleared", () => this.updatePrice(null), {
      signal: this.abortController.signal,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    unregisterPromotionalPrice(this);
  }

  isPromotionActive() {
    const now = Math.floor(Date.now() / 1000);

    return (!this.activeFrom || now >= this.activeFrom) && (!this.activeUntil || now < this.activeUntil);
  }

  refreshVisibility() {
    this.hidden = !this.hasSelectedVariant || !this.isPromotionActive();
  }

  getNextPromotionBoundary(now) {
    return [this.activeFrom, this.activeUntil]
      .filter((timestamp) => timestamp > 0 && timestamp * 1000 > now)
      .map((timestamp) => timestamp * 1000)
      .sort((first, second) => first - second)[0];
  }

  updatePrice(variant) {
    this.hasSelectedVariant = Boolean(variant);
    this.refreshVisibility();

    if (!variant) {
      return;
    }

    const multiplier = (100 - this.discountPercentage) / 100;
    const promotionalPrice = Math.round(variant.price * multiplier);
    const currencyFormat = window.themeVariables.settings.currencyCodeEnabled
      ? window.themeVariables.settings.moneyWithCurrencyFormat
      : window.themeVariables.settings.moneyFormat;

    this.priceElement.replaceChildren(
      document.createRange().createContextualFragment(formatMoney(promotionalPrice, currencyFormat)),
    );
  }
}

if (!window.customElements.get("promotional-price")) {
  window.customElements.define("promotional-price", PromotionalPrice);
}
